import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GameLandingPanel from "./components/GameLandingPanel";
import SavedQuestionsScreen from "./components/SavedQuestionsScreen";
import LeaderboardScreen from "./components/LeaderboardScreen";
import { useAuth } from "../../contexts/AuthContext";
import { useStarredQuestions } from "./hooks/useStarredQuestions";
import { createGameSessionWithOptions, joinCustomQuizByCode, previewCustomQuiz, SharedQuizMeta } from "./api/gameApi";
import { QUESTIONS_PER_ROUND } from "./constants";
import CustomQuizLobby from "./components/CustomQuizLobby";


const LANDING_FADE_DURATION_MS = 420;
const LANDING_REVEAL_DELAY_MS = 60;
const LANDING_EXIT_DURATION_MS = 320;

const GameLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { starredQuestions, removeQuestion } = useStarredQuestions(user?.id ?? null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCustomQuizModal, setShowCustomQuizModal] = useState(false);
  const [customQuizCode, setCustomQuizCode] = useState('');
  const [customQuizError, setCustomQuizError] = useState<string | null>(null);
  const [isJoiningCustomQuiz, setIsJoiningCustomQuiz] = useState(false);
  const [activeScreen, setActiveScreen] = useState<'landing' | 'saved' | 'leaderboard' | 'customLobby'>('landing');
  const [customLobbyData, setCustomLobbyData] = useState<{ quiz: SharedQuizMeta; joinCode: string } | null>(null);
  const [customLobbyError, setCustomLobbyError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsVisible(true);
      setIsOverlayVisible(false);
    }, LANDING_REVEAL_DELAY_MS);

    return () => clearTimeout(revealTimer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lobbyCode = params.get('customLobby');
    if (!lobbyCode) return;
  
    let cancelled = false;
  
    (async () => {
      try {
        const quiz = await previewCustomQuiz(lobbyCode);
        if (cancelled) return;
        setCustomLobbyData({ quiz, joinCode: lobbyCode });
        setCustomLobbyError(null);
        setActiveScreen('customLobby');
      } catch (err) {
        console.error('Failed to reopen custom quiz lobby:', err);
      } finally {
        navigate('/game', { replace: true });
      }
    })();
  
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOpeningTransition = (mode: 'short' | 'long', extraParams?: Record<string, string>) => {
    if (isLeaving) {
      return;
    }

    setIsLeaving(true);
    setIsVisible(false);
    setIsOverlayVisible(true);

    window.setTimeout(() => {
      const params = new URLSearchParams({ mode });
      if (extraParams) {
        Object.entries(extraParams).forEach(([key, value]) => {
          params.set(key, value);
        });
      }
      navigate(`/game/opening?${params.toString()}`);
    }, LANDING_EXIT_DURATION_MS);
  };

  const handleQuickPlay = () => {
    startOpeningTransition('short');
  };

  const handleLongPlay = async () => {
    if (isLeaving) return;

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      const session = await createGameSessionWithOptions(QUESTIONS_PER_ROUND, 'long', false);
      setIsLeaving(true);
      setIsVisible(false);
      setIsOverlayVisible(true);
      window.setTimeout(() => {
        const params = new URLSearchParams({
          mode: 'long',
          session_id: session.session_id,
        });
        navigate(`/game/play/long/${session.session_id}?${params.toString()}`);
      }, LANDING_EXIT_DURATION_MS);
    } catch (err) {
      console.error('Failed to preload long session, continuing without preloaded session.', err);
      startOpeningTransition('long');
    }
  };

  const handleOpenCustomQuizModal = () => {
    setCustomQuizCode('');
    setCustomQuizError(null);
    setShowCustomQuizModal(true);
  };

  const handleStartCustomQuiz = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isJoiningCustomQuiz || isLeaving) return;

    const trimmedCode = customQuizCode.trim();
    if (!trimmedCode) {
      setCustomQuizError('Please enter a game code.');
      return;
    }

    if (!isAuthenticated) {
      setCustomQuizError('Please log in to play custom games.');
      return;
    }

    setCustomQuizError(null);
    setIsJoiningCustomQuiz(true);

    try {
      const quiz = await previewCustomQuiz(trimmedCode);
      setCustomLobbyData({ quiz, joinCode: trimmedCode });
      setCustomLobbyError(null);
      setShowCustomQuizModal(false);
      setActiveScreen('customLobby');
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Invalid code. Please check and try again.';
      setCustomQuizError(message);
    } finally {
      setIsJoiningCustomQuiz(false);
    }
  };

  const handleStartFromLobby = async () => {
    if (!customLobbyData || isLeaving) return;
  
    try {
      const joined = await joinCustomQuizByCode(customLobbyData.joinCode);
      sessionStorage.setItem('game:customQuizJoinCode', customLobbyData.joinCode);
      const query = new URLSearchParams({
        mode: 'short',
        session_id: joined.session_id,
        custom: '1',
      }).toString();
  
      setIsLeaving(true);
      setIsVisible(false);
      setIsOverlayVisible(true);
  
      window.setTimeout(() => {
        navigate(`/game/opening?${query}`);
      }, LANDING_EXIT_DURATION_MS);
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Could not start this game. Please try again.';
      setCustomLobbyError(message);
    }
  };

  if (activeScreen === 'customLobby' && customLobbyData) {
    return (
      <CustomQuizLobby
        quiz={customLobbyData.quiz}
        joinCode={customLobbyData.joinCode}
        error={customLobbyError}
        onStart={handleStartFromLobby}
        onBack={() => {
          setActiveScreen('landing');
          setCustomLobbyData(null);
          setCustomLobbyError(null);
        }}
      />
    );
  }  

  if (activeScreen === 'saved') {
    return (
      <SavedQuestionsScreen
        isAuthenticated={isAuthenticated}
        questions={starredQuestions}
        onRemove={removeQuestion}
        onBack={() => setActiveScreen('landing')}
      />
    );
  }

  if (activeScreen === 'leaderboard') {
    return <LeaderboardScreen onBack={() => setActiveScreen('landing')} />;
  }

  const landingBody = (
    <>
      <span style={{ fontSize: 'clamp(14px, 3.2vw, 20px)', fontWeight: 600 }}>
        The Earth Council demands answers for the state of your planet.
        <br />
        Each year, Earth Guardians present a case against humanity&apos;s stewardship of Earth.
      </span>
      <br />
      <br />
      <span
        style={{
          color: '#FFE896',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(16px, 3.8vw, 28px)',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
        }}
      >
        How many years can humanity survive the trial?
      </span>
    </>
  );

  return (
    <div
      className="relative min-h-screen flex items-center justify-center lg:justify-end bg-cover bg-center overflow-hidden px-4 lg:pr-4 lg:pl-28"
      style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/backgrounds/game-landing-bg.svg')",
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${LANDING_FADE_DURATION_MS}ms ease-out`,
        }}
      />

      <div
        className="relative z-10"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(18px)',
          transition: `opacity ${LANDING_FADE_DURATION_MS}ms ease-out, transform ${LANDING_FADE_DURATION_MS}ms ease-out`,
        }}
      >
        <GameLandingPanel
          header="Earth on Trial"
          body={landingBody}
          className={showLoginModal ? "blur-sm" : ""}
        >
          <div className="flex flex-col items-center gap-4">
            <button
              onClick={handleOpenCustomQuizModal}
              disabled={isLeaving}
              className="px-10 py-3 rounded-full bg-blue-500 hover:bg-blue-600 transition font-semibold text-lg text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Play Custom Game
            </button>
            <div className="flex justify-center gap-3 flex-wrap">
              <button
                onClick={handleQuickPlay}
                disabled={isLeaving}
                className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Quick Play
              </button>
              <button
                onClick={handleLongPlay}
                disabled={isLeaving}
                className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enter Full Trial
              </button>
              <button
                onClick={() => setActiveScreen('saved')}
                className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Saved Questions
              </button>
              <button
                onClick={() => setActiveScreen('leaderboard')}
                className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Leaderboard
              </button>
            </div>
          </div>
        </GameLandingPanel>
      </div>

      <div
        className="pointer-events-none absolute inset-0 z-20 bg-[#020617]"
        style={{
          opacity: isOverlayVisible ? 1 : 0,
          transition: `opacity ${LANDING_FADE_DURATION_MS}ms ease-out`,
        }}
      />

      {showLoginModal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 z-20">
          <div className="bg-[#1b2a41] text-white rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center">
            <h2 className="text-xl font-bold mb-3 font-caudex">Login Required</h2>
            <p className="text-sm text-white/70 mb-6 font-caudex">
              The Extended Game tracks your progress across years. Log in to save your stats and compete on the leaderboard.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-5 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm font-caudex"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate("/login?next=/game/character-select?mode=long")}
                className="px-5 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm font-caudex"
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomQuizModal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-sm px-4 z-20">
          <div className="bg-[#1b2a41] text-white rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center">
            <h2 className="text-xl font-bold mb-3 font-caudex">Play Custom Game</h2>
            <p className="text-sm text-white/70 mb-4 font-caudex">
              Enter your custom game code to start playing.
            </p>
            <form onSubmit={handleStartCustomQuiz} className="space-y-4 text-left">
              <input
                value={customQuizCode}
                onChange={(event) => setCustomQuizCode(event.target.value)}
                placeholder="Enter code"
                className="w-full px-3 py-2 rounded-lg bg-[#27315D] border border-[#2E3A6B] text-white placeholder:text-white/45 outline-none focus:ring-2 focus:ring-cyan-300/40"
                maxLength={30}
                autoFocus
              />
              {customQuizError ? (
                <p className="text-sm text-amber-200 font-caudex">{customQuizError}</p>
              ) : null}
              {!isAuthenticated && (
                <button
                  type="button"
                  onClick={() => navigate('/login?next=/game')}
                  className="w-full px-4 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
                >
                  Log In to Continue
                </button>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCustomQuizModal(false)}
                  className="px-4 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isJoiningCustomQuiz || isLeaving}
                  className="px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isJoiningCustomQuiz ? 'Starting...' : 'Enter Lobby'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameLandingPage;
